import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('powr-token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('powr-token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (err) {
          // Token invalid, clear it
          localStorage.removeItem('powr-token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const signup = async (name, email, password, building = null, referral_code = null) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        name,
        email,
        password,
        building,
        referral_code
      });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('powr-token', access_token);
      setToken(access_token);
      setUser(userData);
      return { success: true, user: userData };
    } catch (err) {
      const message = err.response?.data?.detail || 'Signup failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('powr-token', access_token);
      setToken(access_token);
      setUser(userData);
      return { success: true, user: userData };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('powr-token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (updates) => {
    setError(null);
    try {
      const response = await axios.put(`${API}/auth/me`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (err) {
      const message = err.response?.data?.detail || 'Update failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (err) {
      // Token might be invalid
      logout();
    }
  };

  // Role checking helpers
  const isAuthenticated = !!user;
  const isFreeMember = user?.role === 'free_member' || user?.role === 'paid_member' || user?.role === 'admin';
  const isPaidMember = user?.role === 'paid_member' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';

  // Get auth header for API calls
  const getAuthHeader = () => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      error,
      signup,
      login,
      logout,
      updateProfile,
      refreshUser,
      isAuthenticated,
      isFreeMember,
      isPaidMember,
      isAdmin,
      getAuthHeader
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
